import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageTransition } from "@/hooks/usePageTransition";
import IntakeTransitionOverlay from "@/components/feature/IntakeTransitionOverlay";
import IntakeProgressBar from "@/components/feature/IntakeProgressBar";
import { insuranceProviders } from "@/mocks/intake-pages";
import { getTotalIntakeSteps } from "@/lib/stage1Questionnaire";

export default function IntakeAccount() {
  const { phase, goTo, containerClasses } = usePageTransition();
  const [submitting, setSubmitting] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const totalSteps = getTotalIntakeSteps();

  const [form, setForm] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    email: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    dateOfBirth: "",
    insuranceProvider: "",
    policyId: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.username.trim()) errs.username = "Choose a username";
    else if (form.username.trim().length < 4)
      errs.username = "Username must be at least 4 characters";

    if (!form.password) errs.password = "Create a password";
    else if (form.password.length < 8)
      errs.password = "Password must be at least 8 characters";

    if (!form.confirmPassword)
      errs.confirmPassword = "Please confirm your password";
    else if (form.password !== form.confirmPassword)
      errs.confirmPassword = "Passwords do not match";

    if (!form.email.trim()) errs.email = "Email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Enter a valid email address";

    if (!form.fullName.trim()) errs.fullName = "Full legal name is required";
    if (!form.streetAddress.trim())
      errs.streetAddress = "Street address is required";
    if (!form.city.trim()) errs.city = "City is required";
    if (!form.state.trim()) errs.state = "State is required";
    if (!form.zipCode.trim()) errs.zipCode = "ZIP code is required";
    if (!form.dateOfBirth) errs.dateOfBirth = "Date of birth is required";
    if (!form.insuranceProvider)
      errs.insuranceProvider = "Please select your insurance provider";
    if (!form.policyId.trim()) errs.policyId = "Policy ID is required";
    return errs;
  };

  const handleCreateAccount = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);

    // Save all prior intake data + new account fields
    sessionStorage.setItem("intake_username", form.username);
    sessionStorage.setItem("intake_email", form.email);
    sessionStorage.setItem("intake_fullName", form.fullName);
    sessionStorage.setItem("intake_dob", form.dateOfBirth);
    sessionStorage.setItem("intake_zip", form.zipCode);
    sessionStorage.setItem("intake_insurance", form.insuranceProvider);
    sessionStorage.setItem("intake_policyId", form.policyId);

    try {
      const params = new URLSearchParams();
      params.append("username", form.username);
      params.append("email", form.email);
      params.append("full_name", form.fullName);
      params.append("street_address", form.streetAddress);
      params.append("city", form.city);
      params.append("state", form.state);
      params.append("zip_code", form.zipCode);
      params.append("date_of_birth", form.dateOfBirth);
      params.append("insurance_provider", form.insuranceProvider);
      params.append("policy_id", form.policyId);
      params.append("persona", sessionStorage.getItem("intake_persona") || "");
      params.append(
        "amputation_type",
        sessionStorage.getItem("intake_amputation_type") || "",
      );
      params.append(
        "surgical_status",
        sessionStorage.getItem("intake_surgical_status") || "",
      );
      params.append(
        "mobility",
        sessionStorage.getItem("intake_mobility") || "",
      );

      await fetch("https://readdy.ai/api/form/d8hk3eliodfui947u1qg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
    } catch {
      // silently handle
    }

    setTimeout(() => {
      setSubmitting(false);
      setAccountCreated(true);
    }, 900);
  };

  const handleProceedToVerification = () => {
    goTo("/intake/myfit-scan");
  };

  return (
    <div className="h-screen flex flex-col bg-background-50">
      <IntakeTransitionOverlay />
      {/* Top Bar: Back + Progress */}
      <div className="flex-shrink-0 bg-white border-b border-background-100">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-6 py-4">
            <Link
              to="/intake/klevel-blueprint"
              className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              Back
            </Link>
          </div>

          <div className="pb-4">
            <IntakeProgressBar
              currentStep={totalSteps - 2}
              totalSteps={totalSteps}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex items-start justify-center overflow-y-auto px-4 md:px-6 py-8 md:py-12 transition-all duration-500 ease-out relative ${containerClasses}`}
      >
        {/* Warm Background Blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-accent-100/20 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-primary-100/12 blur-3xl"></div>
        </div>

        <div className="w-full max-w-[1100px] mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-stretch">
            {/* LEFT PANEL */}
            <div className="lg:w-[55%] flex flex-col justify-center">
              {!accountCreated ? (
                /* ---------- FORM STATE ---------- */
                <div className="bg-white rounded-2xl border border-background-100 p-8 md:p-10 animate-fadeSlideIn">
                  <div className="mb-7">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-user-add-line text-sm"></i>
                      </span>
                      <h1 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                        Create Your Account
                      </h1>
                    </div>
                    <p className="text-xs md:text-sm text-foreground-500">
                      Almost there. Create your secure account to save all your
                      intake information and connect with your care team.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {/* Username & Email Row */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                          Username
                        </label>
                        <input
                          type="text"
                          value={form.username}
                          onChange={(e) =>
                            updateField("username", e.target.value)
                          }
                          placeholder="Choose a username"
                          className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                            errors.username
                              ? "border-red-300 bg-red-50 focus:ring-red-100"
                              : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                          }`}
                        />
                        {errors.username && (
                          <p className="text-[10px] text-red-500 mt-1 font-medium">
                            {errors.username}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={(e) => updateField("email", e.target.value)}
                          placeholder="you@example.com"
                          className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                            errors.email
                              ? "border-red-300 bg-red-50 focus:ring-red-100"
                              : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                          }`}
                        />
                        {errors.email && (
                          <p className="text-[10px] text-red-500 mt-1 font-medium">
                            {errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Password Row */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                          Create Password
                        </label>
                        <input
                          type="password"
                          value={form.password}
                          onChange={(e) =>
                            updateField("password", e.target.value)
                          }
                          placeholder="Min. 8 characters"
                          className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                            errors.password
                              ? "border-red-300 bg-red-50 focus:ring-red-100"
                              : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                          }`}
                        />
                        {errors.password && (
                          <p className="text-[10px] text-red-500 mt-1 font-medium">
                            {errors.password}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          value={form.confirmPassword}
                          onChange={(e) =>
                            updateField("confirmPassword", e.target.value)
                          }
                          placeholder="Re-enter your password"
                          className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                            errors.confirmPassword
                              ? "border-red-300 bg-red-50 focus:ring-red-100"
                              : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                          }`}
                        />
                        {errors.confirmPassword && (
                          <p className="text-[10px] text-red-500 mt-1 font-medium">
                            {errors.confirmPassword}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Section Divider */}
                    <div className="flex items-center gap-3 pt-1 pb-1">
                      <div className="flex-1 h-px bg-background-100"></div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400 whitespace-nowrap">
                        Profile Details
                      </span>
                      <div className="flex-1 h-px bg-background-100"></div>
                    </div>

                    {/* Full Legal Name */}
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                        Full Legal Name
                      </label>
                      <input
                        type="text"
                        value={form.fullName}
                        onChange={(e) =>
                          updateField("fullName", e.target.value)
                        }
                        placeholder="As it appears on your insurance card"
                        className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                          errors.fullName
                            ? "border-red-300 bg-red-50 focus:ring-red-100"
                            : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                        }`}
                      />
                      {errors.fullName && (
                        <p className="text-[10px] text-red-500 mt-1 font-medium">
                          {errors.fullName}
                        </p>
                      )}
                    </div>

                    {/* Address */}
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={form.streetAddress}
                        onChange={(e) =>
                          updateField("streetAddress", e.target.value)
                        }
                        placeholder="123 Main Street"
                        className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                          errors.streetAddress
                            ? "border-red-300 bg-red-50 focus:ring-red-100"
                            : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                        }`}
                      />
                      {errors.streetAddress && (
                        <p className="text-[10px] text-red-500 mt-1 font-medium">
                          {errors.streetAddress}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                          City
                        </label>
                        <input
                          type="text"
                          value={form.city}
                          onChange={(e) => updateField("city", e.target.value)}
                          placeholder="Tyler"
                          className={`w-full px-3 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                            errors.city
                              ? "border-red-300 bg-red-50 focus:ring-red-100"
                              : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                          }`}
                        />
                        {errors.city && (
                          <p className="text-[10px] text-red-500 mt-1 font-medium">
                            {errors.city}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                          State
                        </label>
                        <input
                          type="text"
                          value={form.state}
                          onChange={(e) => updateField("state", e.target.value)}
                          placeholder="TX"
                          maxLength={2}
                          className={`w-full px-3 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all uppercase ${
                            errors.state
                              ? "border-red-300 bg-red-50 focus:ring-red-100"
                              : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                          }`}
                        />
                        {errors.state && (
                          <p className="text-[10px] text-red-500 mt-1 font-medium">
                            {errors.state}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                          ZIP Code
                        </label>
                        <input
                          type="text"
                          value={form.zipCode}
                          onChange={(e) =>
                            updateField("zipCode", e.target.value)
                          }
                          placeholder="75701"
                          maxLength={10}
                          className={`w-full px-3 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                            errors.zipCode
                              ? "border-red-300 bg-red-50 focus:ring-red-100"
                              : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                          }`}
                        />
                        {errors.zipCode && (
                          <p className="text-[10px] text-red-500 mt-1 font-medium">
                            {errors.zipCode}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* DOB */}
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                        Date of Birth (DOB)
                      </label>
                      <input
                        type="date"
                        value={form.dateOfBirth}
                        onChange={(e) =>
                          updateField("dateOfBirth", e.target.value)
                        }
                        className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all cursor-pointer ${
                          errors.dateOfBirth
                            ? "border-red-300 bg-red-50 focus:ring-red-100"
                            : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                        }`}
                      />
                      {errors.dateOfBirth && (
                        <p className="text-[10px] text-red-500 mt-1 font-medium">
                          {errors.dateOfBirth}
                        </p>
                      )}
                    </div>

                    {/* Insurance Provider */}
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                        Primary Insurance Provider
                      </label>
                      <select
                        value={form.insuranceProvider}
                        onChange={(e) =>
                          updateField("insuranceProvider", e.target.value)
                        }
                        className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all cursor-pointer ${
                          errors.insuranceProvider
                            ? "border-red-300 bg-red-50 focus:ring-red-100"
                            : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                        }`}
                      >
                        <option value="" className="text-foreground-400">
                          Select your insurance provider
                        </option>
                        {insuranceProviders.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                      {errors.insuranceProvider && (
                        <p className="text-[10px] text-red-500 mt-1 font-medium">
                          {errors.insuranceProvider}
                        </p>
                      )}
                    </div>

                    {/* Policy ID */}
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                        Policy ID Number
                      </label>
                      <input
                        type="text"
                        value={form.policyId}
                        onChange={(e) =>
                          updateField("policyId", e.target.value)
                        }
                        placeholder="As shown on your insurance card"
                        className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                          errors.policyId
                            ? "border-red-300 bg-red-50 focus:ring-red-100"
                            : "border-background-200 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                        }`}
                      />
                      {errors.policyId && (
                        <p className="text-[10px] text-red-500 mt-1 font-medium">
                          {errors.policyId}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Security Note */}
                  <div className="mt-5 flex items-center gap-2 px-4 py-3 rounded-xl bg-background-50 border border-background-100">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400">
                      <i className="ri-shield-check-line text-xs"></i>
                    </span>
                    <p className="text-[10px] text-foreground-500 leading-relaxed">
                      Your information is encrypted and HIPAA-compliant. We
                      never share your data without your explicit consent.
                    </p>
                  </div>

                  {/* Submit */}
                  <div className="mt-6">
                    {!submitting ? (
                      <button
                        type="button"
                        onClick={handleCreateAccount}
                        className="w-full py-4 rounded-xl bg-primary-500 text-background-50 text-sm md:text-base font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-user-add-line text-sm"></i>
                        </span>
                        Create Account &amp; Save Profile
                      </button>
                    ) : (
                      <div className="w-full py-4 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center gap-3 animate-fadeSlideIn">
                        <span className="w-5 h-5 flex items-center justify-center text-green-600">
                          <i className="ri-loader-4-line text-sm animate-spin"></i>
                        </span>
                        <span className="text-sm font-bold text-green-700">
                          Creating your secure account...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* ---------- POST-CREATION SUCCESS STATE ---------- */
                <div className="bg-white rounded-2xl border border-background-100 p-8 md:p-10 animate-fadeSlideIn">
                  {/* Success Header */}
                  <div className="text-center mb-7">
                    <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="w-8 h-8 flex items-center justify-center text-green-600">
                        <i className="ri-check-double-line text-2xl"></i>
                      </span>
                    </div>
                    <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                      Your account is ready!
                    </h1>
                    <p className="text-sm text-foreground-500 leading-relaxed max-w-md mx-auto">
                      All your intake information has been saved securely.
                      There&apos;s one more thing before we can connect you with
                      a physician.
                    </p>
                  </div>

                  {/* Next Step Preview */}
                  <div className="mb-6">
                    <div className="flex items-start gap-4 mb-5">
                      <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-camera-lens-line text-lg"></i>
                      </span>
                      <div>
                        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-2">
                          Scans &amp; Photos of Your Residual Limb(s)
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                          The physician will need to see detailed scans and
                          photos of your residual limb(s) to complete your
                          evaluation. This must be completed before your
                          telehealth consult can be scheduled.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Privacy Note */}
                  <div className="flex items-start gap-3 px-4 py-4 rounded-xl bg-background-50 border border-background-100 mb-6">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
                      <i className="ri-information-line text-sm"></i>
                    </span>
                    <p className="text-[11px] text-foreground-500 leading-relaxed">
                      All scans and images are encrypted and shared only with
                      your assigned physician. Next, you&apos;ll use MyFit to
                      capture precise 3D metrics using just your phone.
                    </p>
                  </div>

                  {/* CTA */}
                  <button
                    type="button"
                    onClick={handleProceedToVerification}
                    className="w-full py-4 rounded-xl bg-primary-500 text-background-50 text-sm md:text-base font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                    Continue to Next Step
                  </button>
                </div>
              )}
            </div>

            {/* RIGHT PANEL */}
            <div className="lg:w-[45%] flex">
              {!accountCreated ? (
                <div className="bg-white rounded-2xl border border-background-100 p-6 md:p-8 flex flex-col w-full animate-slideUpIn">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-800 mb-4 self-start">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-play-circle-line text-[9px]"></i>
                    </span>
                    Your Journey
                  </span>

                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">
                    Why We Need These Details
                  </h3>

                  <div className="rounded-xl overflow-hidden mb-4 flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=warm%20bright%20modern%20home%20office%20scene%20with%20laptop%20open%20displaying%20health%20dashboard%20on%20screen%20soft%20natural%20morning%20sunlight%20through%20window%20person%20sitting%20comfortably%20in%20chair%20with%20coffee%20mug%20calm%20organized%20desk%20setting%20neutral%20cream%20beige%20tones%20editorial%20lifestyle%20photography%20feeling%20of%20security%20and%20peace%20of%20mind%20healthcare%20technology%20aesthetic&width=600&height=340&seq=intake-ad-page6-account&orientation=landscape"
                      alt="Creating your secure health profile from home"
                      className="w-full h-auto"
                    />
                  </div>

                  <p className="text-sm text-foreground-500 leading-relaxed flex-1">
                    Your profile is the foundation of everything. Your insurance
                    details let us generate precise coverage estimates. Your
                    address connects you with nearby partner clinics. Your date
                    of birth confirms eligibility. Every field helps us build a
                    tailored plan — so you spend less time on paperwork and more
                    time getting back to walking.
                  </p>

                  <div className="mt-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-background-50 border border-background-100">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-primary-500">
                      <i className="ri-lock-line text-sm"></i>
                    </span>
                    <p className="text-[11px] text-foreground-500 leading-relaxed">
                      End-to-end encrypted · HIPAA compliant · You control who
                      sees your data
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-background-100 p-6 md:p-8 flex flex-col w-full animate-slideUpIn">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-800 mb-4 self-start">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-play-circle-line text-[9px]"></i>
                    </span>
                    What&apos;s Next
                  </span>

                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">
                    Preparing for Your Physician Review
                  </h3>

                  <div className="rounded-xl overflow-hidden mb-4 flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=person%20sitting%20comfortably%20on%20modern%20sofa%20at%20home%20using%20smartphone%20to%20photograph%20their%20leg%20in%20bright%20natural%20window%20light%20calm%20organized%20living%20room%20warm%20neutral%20cream%20tones%20soft%20genuine%20atmosphere%20editorial%20lifestyle%20photography%20healthcare%20at%20home%20concept%20clean%20composition%20peaceful%20environment&width=600&height=340&seq=intake-ad-page6-post-account&orientation=landscape"
                      alt="Preparing limb photos from the comfort of home"
                      className="w-full h-auto"
                    />
                  </div>

                  <p className="text-sm text-foreground-500 leading-relaxed flex-1">
                    The physician needs clear images of your residual limb(s) to
                    assess healing, skin condition, and socket fit requirements.
                    Our guided photo capture makes it easy — simple step-by-step
                    instructions, taken privately from home. These images are
                    encrypted and shared only with your assigned physician. Once
                    submitted, you can schedule your telehealth consult
                    immediately.
                  </p>

                  <div className="mt-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-primary-50 border border-primary-100">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-primary-600">
                      <i className="ri-time-line text-sm"></i>
                    </span>
                    <p className="text-[11px] text-primary-700 leading-relaxed font-medium">
                      Takes less than 5 minutes — all you need is your phone.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Footer */}
      <div className="w-full border-t border-background-100 bg-background-50">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
              <i className="ri-information-line text-[10px]"></i>
            </span>
            <p className="text-[10px] text-foreground-400 leading-relaxed max-w-3xl">
              Notice: This platform is an administrative optimization tool. All
              AI-generated functional assessments, financial forecasts,
              wholesale cost aggregations, advertising recommendations, 3D
              metric visualizations, and documentation templates are strictly
              informational. Final clinical specifications, billing submittals,
              prescriptions, and custom fabrication workflows require formal,
              independent review and explicit sign-off by a licensed physician
              and a Certified Prosthetist prior to patient care.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
